<script setup lang="tsx">
import type { Json } from '@/supabase/types';

import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { useRouter } from 'vue-router';

import { supabase } from '../../supabase';
import { getMessage } from '../../utils/exception';

const emit = defineEmits<{
  error: [message: string];
}>();

const dialog = ref(false);
const dialogValid = ref<boolean | null>(null);
const editedIndex = ref<number | null>(null);

const dialogDelete = ref(false);

const router = useRouter();

interface Row {
  user: string | null;

  user_email: string | null;

  user_card: { [key: string]: Json | undefined } | null;
}

interface Data {
  id: string | null;
  name: string;
  email: string;
}

const defaultItem = ref<Data>({
  id: null,
  email: '',
  name: '',
});
const editedItem = ref<Data>();

const dialogTitle = computed(() => {
  return editedIndex.value === null ? 'New Superadmin' : 'Edit Superadmin';
});

const busy = ref(true);

const itemsPerPageOptions = [5, -1];

const items = ref<Row[]>([]);

const headers = ref([
  {
    title: 'Name',
    align: 'start' as const,
    sortable: true,
    key: 'user_card.fn',
  },
  { title: 'Email', key: 'user_email' },
  { title: 'Actions', key: 'actions', sortable: false },
]);

const rules = {
  required: (value: string) => !!value || 'Field is required',
  email: (value: string) =>
    /.[^\n\r@\u2028\u2029]*@.+\..{2,}/.test(value)
    || 'Please enter an email address',
};

async function load() {
  try {
    busy.value = true;
    const { data, error } = await supabase
      .from('user_role_details')
      .select()
      .eq('role_name', 'superadmin');
    busy.value = false;
    if (error) throw error;
    items.value = data as Row[];
  }
  catch (error) {
    emit('error', getMessage(error));
  }
}

defineExpose({ load });

onMounted(load);

function editedItemFromRow(item: Row) {
  let name = '';
  if (
    item.user_card
    && typeof item.user_card === 'object'
    && 'fn' in item.user_card
  ) {
    name = item.user_card.fn as string;
  }
  return {
    id: item.user,
    email: item.user_email ?? '',
    name,
  };
}

function editItem(item: Row) {
  editedIndex.value = items.value.indexOf(item);
  editedItem.value = editedItemFromRow(item);
  dialog.value = true;
}

function resetEditedItem() {
  editedIndex.value = null;
  editedItem.value = Object.assign({} as Data, defaultItem.value);
}

watch(
  dialog,
  (value) => {
    if (!value) {
      nextTick(resetEditedItem);
    }
  },
  { immediate: true },
);

watch(dialogDelete, (value) => {
  if (!value) {
    nextTick(resetEditedItem);
  }
});

async function save() {
  if (!dialogValid.value) return;
  if (!editedItem.value) return;

  try {
    if (editedItem.value.id !== null && editedIndex.value !== null) {
      const { error } = await supabase
        .from('person')
        .update({
          card: {
            ...items.value[editedIndex.value].user_card,
            fn: editedItem.value.name,
          },
        })
        .eq('id', editedItem.value.id);
      if (error) throw error;
    }
    else {
      const redirectUrl = new URL(
        router.resolve({ name: 'superadmin' }).href,
        window.location.origin,
      ).toString();
      const { error } = await supabase.rpc('create_superadmin', {
        email: editedItem.value.email,
        name: editedItem.value.name,
        redirect_to: redirectUrl,
      });
      if (error) throw error;
    }
    load();
  }
  catch (error) {
    console.error(error);
    emit('error', getMessage(error));
  }
  finally {
    dialog.value = false;
  }
}

function deleteItem(item: Row) {
  editedIndex.value = items.value.indexOf(item);
  editedItem.value = editedItemFromRow(item);
  dialogDelete.value = true;
}

async function deleteItemConfirm() {
  if (!editedItem.value?.id) return;
  try {
    const { error } = await supabase.rpc('remove_superadmin', {
      user_id: editedItem.value.id,
    });
    if (error) throw error;
    load();
  }
  catch (error) {
    console.error(error);
    emit('error', getMessage(error));
  }
  finally {
    dialogDelete.value = false;
  }
}
</script>

<template>
  <v-data-table
    items-per-page="5"
    :items-per-page-options="itemsPerPageOptions"
    :items="items"
    :headers="headers"
    :loading="busy"
  >
    <template #top>
      <v-toolbar flat>
        <v-dialog v-model="dialog" max-width="50em">
          <template #activator="{ props }">
            <v-btn color="primary" v-bind="props" prepend-icon="mdi-plus">
              Add
            </v-btn>
            <v-spacer />
          </template>
          <v-card>
            <v-card-title>
              <span class="text-headline-small">{{ dialogTitle }}</span>
            </v-card-title>

            <v-form
              v-model="dialogValid"
              validate-on="invalid-input"
              @submit.prevent="save"
            >
              <v-card-text>
                <v-container v-if="editedItem">
                  <v-row>
                    <v-col cols="12" md="6" sm="12">
                      <v-text-field
                        v-model="editedItem.name"
                        :rules="[rules.required]"
                        label="Full Name"
                      />
                    </v-col>
                    <v-col cols="12" md="6" sm="12">
                      <div
                        v-tooltip="{
                          disabled: editedIndex === null,
                          text: 'Email cannot be changed',
                        }"
                      >
                        <v-text-field
                          v-model="editedItem.email"
                          label="Email"
                          type="email"
                          :rules="[rules.email]"
                          :disabled="editedIndex !== null"
                        />
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="blue-darken-1"
                  variant="text"
                  @click="dialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="blue-darken-1"
                  variant="text"
                  type="submit"
                  :disabled="dialogValid === false"
                >
                  Save
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="dialogDelete"
          max-width="50em"
          @keydown.esc="dialogDelete = false"
        >
          <v-card>
            <v-card-title class="text-title-large text-center">
              Confirmation required
            </v-card-title>
            <v-card-text>
              <div class="text-title-large text-center mb-2">
                Really remove
                <span class="text-primary">{{ editedItem?.name }} &lt;{{ editedItem?.email }}&gt;</span>
                as superadmin?
              </div>
              The corresponding user <em>will be deleted</em> if they have no
              other role.
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="blue-darken-1"
                variant="text"
                @click="dialogDelete = false"
              >
                Cancel
              </v-btn>
              <v-btn
                color="blue-darken-1"
                variant="text"
                @click="deleteItemConfirm"
              >
                OK
              </v-btn>
              <v-spacer />
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-btn
          :disabled="busy"
          icon="mdi-refresh"
          aria-label="Refresh"
          color="primary"
          @click="load"
        />
      </v-toolbar>
    </template>
    <template #item.actions="{ item }">
      <v-icon class="me-2" size="small" @click="editItem(item)">
        mdi-pencil
      </v-icon>
      <v-icon size="small" @click="deleteItem(item)">
        mdi-delete
      </v-icon>
    </template>
  </v-data-table>
</template>
